import { useState, useEffect } from 'react';
import nvgtrLogoBlack from '../assets/nvgtrLogo_Black.svg';
import nvgtrLogoWhite from '../assets/nvgtrLogo_White.svg';

const SIDEBAR_CACHE_KEY = 'sidebarLogoCache';

/**
 * Returns the company logo from the Sidebar's cache. No fetch - just reads
 * sidebarLogoCache that the Sidebar already populates. Returns { light, dark }
 * for use with dark:hidden / hidden dark:block pattern.
 * Subscribes to sidebar-logo-preference-changed so AccountModal etc. re-render
 * when the Sidebar updates the cache.
 */
export const useFullLogo = () => {
  const [, setTick] = useState(0);

  useEffect(() => {
    let timeoutId;
    const handler = () => {
      setTick((t) => t + 1);
      clearTimeout(timeoutId);
      // Sidebar updates cache async; trigger another re-render after it finishes
      timeoutId = setTimeout(() => setTick((t) => t + 1), 400);
    };
    window.addEventListener('sidebar-logo-preference-changed', handler);
    // Re-render when cache updates (e.g. theme change) so logo switches to company's opposite BM
    window.addEventListener('sidebar-logo-cache-updated', handler);
    return () => {
      window.removeEventListener('sidebar-logo-preference-changed', handler);
      window.removeEventListener('sidebar-logo-cache-updated', handler);
      clearTimeout(timeoutId);
    };
  }, []);

  try {
    const cached = localStorage.getItem(SIDEBAR_CACHE_KEY);
    if (cached) {
      const { logoUrl, logoUrlLight, logoUrlDark, isDark, hasCustomAssets } = JSON.parse(cached);
      if (hasCustomAssets && (logoUrlLight || logoUrlDark || logoUrl)) {
        // Prefer explicit light/dark URLs so theme switch uses company's opposite BM
        // instead of falling back to nvgtr default (matches Sidebar behavior)
        return {
          light: logoUrlLight ?? (isDark ? nvgtrLogoBlack : logoUrl),
          dark: logoUrlDark ?? (isDark ? logoUrl : nvgtrLogoWhite),
        };
      }
    }
  } catch {
    // Fall through to defaults
  }
  return { light: nvgtrLogoBlack, dark: nvgtrLogoWhite };
};
